import React from 'react';
import useSWR from 'swr';
import api from '../../services/api';
import { APP_CONFIG } from '../../config/appConfig';
import { emailPartitionAtSearch } from './utils/emailPartitionAtQuery';
import { effectivePlainTextBody } from './utils/phoenixPlaceholderPlainText';
import { buildFolderImportItems, chunkFolderImportItems } from './utils/folderImportBatching';

function usePageVisible(): boolean {
  const [visible, setVisible] = React.useState(
    () => (typeof document !== 'undefined' ? document.visibilityState === 'visible' : true)
  );
  React.useEffect(() => {
    const handler = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);
  return visible;
}

export interface Email {
  id: number;
  /** Composite PK with id; pass as ?partitionAt= on API routes when known */
  partitionAt?: string;
  emailAccountId: number;
  messageId: string;
  threadId?: string;
  inReplyTo?: string;
  references?: string;
  /** Present when list is grouped by thread */
  threadCount?: number;
  /** Peers in the same thread on the current list page (thread view only) */
  threadListEmails?: EmailThreadMessage[];
  subject?: string;
  from: Array<{ name?: string; email: string }>;
  to: Array<{ name?: string; email: string }>;
  cc?: Array<{ name?: string; email: string }>;
  bcc?: Array<{ name?: string; email: string }>;
  bodyText?: string;
  bodyHtml?: string;
  date: string;
  receivedDate: string;
  isRead: boolean;
  isStarred: boolean;
  isArchived: boolean;
  folder: string;
  sentByUserId?: number | null;
  /** Normalized importance/priority from headers: 'low' | 'normal' | 'high' */
  priority?: string | null;
  attachments?: Array<{
    filename: string;
    contentType: string;
    size: number;
    contentId?: string;
    isInline?: boolean;
  }>;
  /** Persisted attachment rows from GET /emails/:id (text extraction status per file) */
  emailAttachments?: Array<{
    id: number;
    filename: string;
    contentType: string;
    size: number;
    textExtractionStatus: 'PENDING' | 'OK' | 'FAILED' | 'UNSUPPORTED' | 'SKIPPED';
    textExtractionError?: string | null;
    textExtractedAt?: string | null;
  }>;
  linkedClientId?: number;
  linkedTodoId?: number;
  linkedAppointmentId?: number;
  createdAt: string;
  updatedAt: string;
  emailAccount?: {
    id: number;
    emailAddress: string;
    displayName?: string;
  };
  linkedClient?: {
    clientId: number;
    clientName?: string;
  };
  linkedTodo?: {
    todoId: number;
    todoDescr?: string | null;
    todoDueDate?: string | null;
  };
  linkedAppointment?: {
    clientAppId: number;
    clientAppName?: string | null;
    clientAppScheduleDateTime?: string | null;
  };
  /** Who has read this message (from GET /emails/:id); only present in detail response */
  readBy?: Array<{
    userId: number;
    name: string;
    email: string;
    readDate: string;
  }>;
  /** Count of users who have read this message (shared-mail list response) */
  readByCount?: number;
  /** Current user's note on this email (detail response only; legacy) */
  note?: string | null;
  noteCreatedDate?: string | null;
  /** True when the current user has a note on this email (list response) */
  hasNote?: boolean;
  /** Shared-inbox assignee */
  assignedToUserId?: number | null;
  assignedToUser?: { id: number; name: string; email: string } | null;
  /** Shared-inbox workflow status */
  workflowStatus?: EmailWorkflowStatus | null;
  /** True when email has team comments (list response) */
  hasComments?: boolean;
  commentCount?: number;
  /** Raw message headers (detail response; saved when syncing from IMAP) */
  messageHeaders?: string | null;
  /** Tag IDs on this email (list/detail) */
  tagIds?: number[];
  /** Tag objects on this email (list/detail) */
  tags?: { id: number; name: string; color?: string }[];
  /** Present when listing Mentions inbox (mentionedMe=true) */
  latestMentionAt?: string;
  latestMentionAuthor?: string;
  latestMentionSnippet?: string;
  unreadMentionCount?: number;
  hasUnreadMention?: boolean;
}

export type EmailWorkflowStatus = 'open' | 'working' | 'closed';

export interface EmailThreadMessage {
  id: number;
  partitionAt: string;
  subject?: string | null;
  from: Email['from'];
  date: string;
  folder: string;
  isRead: boolean;
  isStarred: boolean;
  isArchived: boolean;
  sentByUserId?: number | null;
}

export interface EmailThreadResponse {
  threadId: string;
  count: number;
  emails: EmailThreadMessage[];
}

export function mapEmailToThreadMessage(email: Email): EmailThreadMessage {
  return {
    id: email.id,
    partitionAt: email.partitionAt ?? '',
    subject: email.subject,
    from: email.from,
    date: email.date,
    folder: email.folder,
    isRead: email.isRead,
    isStarred: email.isStarred,
    isArchived: email.isArchived,
    sentByUserId: email.sentByUserId,
  };
}

export interface EmailComment {
  id: number;
  emailId: number;
  userId: number;
  body: string;
  createdAt: string;
  editedAt?: string | null;
  user: { id: number; name: string; email: string };
}

export interface EmailMentionableUser {
  id: number;
  name: string;
  email: string;
}

export interface EmailMentionableUsersResponse {
  users: EmailMentionableUser[];
  assignee: EmailMentionableUser | null;
}

export interface IcsAppointmentSuggestion {
  summary: string;
  description?: string;
  location?: string;
  start?: string;
  end?: string;
  organizer?: string;
  attendees?: string[];
  attachmentFilename?: string;
}

export interface EmailTag {
  id: number;
  name: string;
  color?: string | null;
  order?: number;
  createdAt: string;
  _count?: { assignments: number };
  isOwner?: boolean;
  shareId?: number;
  ownerName?: string;
  permission?: 'read' | 'write';
}

export type SearchInField = 'sender' | 'recipients' | 'subject' | 'body' | 'attachments';

export type SearchFolderKind =
  | 'search'
  | 'inbox'
  | 'combined'
  | 'sent'
  | 'combinedSent'
  | 'mySent'
  | 'combinedMySent';

export interface EmailSearchFolder {
  id: number;
  userId: number;
  name: string;
  search?: string | null;
  searchIn?: SearchInField[] | null;
  accountId?: number | null;
  accountIds?: number[] | null;
  folderKind?: SearchFolderKind | string | null;
  folder?: string | null;
  periodPreset?: string | null;
  periodYear?: number | null;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailPersonalFolderTreeNode {
  id: number;
  name: string;
  parentId: number | null;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  children: EmailPersonalFolderTreeNode[];
  entryCount?: number;
}

export function flattenPersonalFolders(
  nodes: EmailPersonalFolderTreeNode[],
  depth = 0,
): { id: number; name: string; depth: number }[] {
  const out: { id: number; name: string; depth: number }[] = [];
  for (const node of nodes) {
    out.push({ id: node.id, name: node.name, depth });
    if (node.children?.length) {
      out.push(...flattenPersonalFolders(node.children, depth + 1));
    }
  }
  return out;
}

export interface SharedFolderTreeNode {
  shareId: number;
  folderId: number;
  name: string;
  ownerId: number;
  ownerName: string;
  includeSubfolders: boolean;
  permission: 'read' | 'write';
  entryCount?: number;
  children: SharedFolderTreeNode[];
}

export interface EmailThreadGroup {
  threadId: string;
  count: number;
  latestEmail: Email;
  emails: Email[];
}

export interface EmailsResponse {
  data: Email[] | EmailThreadGroup[];
  total?: number;
  skip: number;
  take: number;
  groupedByThread?: boolean;
  threads?: EmailThreadGroup[];
  hasMore?: boolean;
  nextCursor?: string | null;
  /** Client-measured round-trip time for this list request (ms). */
  loadTimeMs?: number;
}

/** Format folder list load time for the message list footer. */
export function formatFolderLoadTime(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms / 1000)} s`;
}

export interface EmailsListOptions {
  skip?: number;
  take?: number;
  cursor?: string;
  includeTotal?: boolean;
}

export interface EmailsListHookResult {
  data: Email[];
  total: number;
  hasMore: boolean;
  nextCursor: string | null;
  loadTimeMs?: number;
  isLoading: boolean;
  isValidating: boolean;
  isError: unknown;
  mutate: ReturnType<typeof useSWR<EmailsResponse>>['mutate'];
  requestQuery: string;
}

export const useEmails = (searchParams?: string, options?: EmailsListOptions) => {
  const pageVisible = usePageVisible();
  const queryParams = new URLSearchParams(searchParams);
  if (options?.cursor) {
    queryParams.set('cursor', options.cursor);
  } else if (options?.skip !== undefined) {
    queryParams.set('skip', options.skip.toString());
  }
  if (options?.take !== undefined) {
    queryParams.set('take', options.take.toString());
  }
  if (options?.includeTotal) {
    queryParams.set('includeTotal', 'true');
  }
  
  const url = `/emails${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const { data, error, isLoading, isValidating, mutate } = useSWR<EmailsResponse>(
    url,
    async (url: string) => {
      const startedAt = performance.now();
      try {
        const response = await api.get(url, { timeout: APP_CONFIG.api.emailListTimeout });
        if (process.env.NODE_ENV !== 'production') {
          const hasBase = url.includes('baseSearch') || url.includes('baseFolder') || url.includes('searchFolderId');
          if (hasBase) {
          }
        }
        const raw = response.data as EmailsResponse;
        const loadTimeMs = Math.round(performance.now() - startedAt);
        const threadGroups: EmailThreadGroup[] | null =
          raw.groupedByThread && Array.isArray(raw.threads)
            ? raw.threads
            : raw.groupedByThread &&
                Array.isArray(raw.data) &&
                raw.data.length > 0 &&
                typeof raw.data[0] === 'object' &&
                raw.data[0] != null &&
                'latestEmail' in raw.data[0]
              ? (raw.data as EmailThreadGroup[])
              : null;
        if (threadGroups) {
          const flat = threadGroups.map((t) => ({
            ...t.latestEmail,
            threadCount: t.count,
            threadId: t.threadId,
            threadListEmails: t.emails.map(mapEmailToThreadMessage),
          }));
          return { ...raw, data: flat, threads: threadGroups, loadTimeMs };
        }
        return { ...raw, loadTimeMs };
      } catch (err: any) {
        console.error('Emails API Error:', err);
        console.error('Error details:', err?.response?.data);
        throw err;
      }
    },
    {
      // Poll every 30s only when tab is visible; pause when hidden to save requests
      refreshInterval: pageVisible ? 30000 : 0,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  const emails = (data?.data ?? []) as Email[];
  const total = data?.total ?? 0;
  const hasMore = data?.hasMore ?? false;
  const nextCursor = data?.nextCursor ?? null;
  const loadTimeMs = data?.loadTimeMs;

  return {
    data: emails,
    total,
    hasMore,
    nextCursor,
    loadTimeMs,
    isLoading,
    isValidating,
    isError: error,
    mutate,
    /** Query string used for the list request (includes skip/take; strip groupBy for thread scope). */
    requestQuery: queryParams.toString(),
  };
};

const EMAIL_LIST_PAGINATION_PARAMS = ['skip', 'take', 'cursor', 'includeTotal'] as const;

/** Folder/filter scope only — excludes pagination and thread grouping. */
export function emailListScopeKey(requestQuery: string): string {
  const params = new URLSearchParams(requestQuery);
  params.delete('groupBy');
  for (const key of EMAIL_LIST_PAGINATION_PARAMS) {
    params.delete(key);
  }
  return params.toString();
}

/** Derive thread panel scope from the list API query string. */
export function listScopeQueryFromEmailsRequest(requestQuery: string): string {
  return emailListScopeKey(requestQuery);
}

export const useEmail = (id: number, partitionAt?: string | null) => {
  const key =
    id > 0
      ? partitionAt
        ? `/emails/${id}?partitionAt=${encodeURIComponent(partitionAt)}`
        : `/emails/${id}`
      : null;
  const { data, error, isLoading, mutate } = useSWR<Email>(
    key,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    }
  );

  return {
    data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useEmailThread = (
  id: number,
  partitionAt?: string | null,
  listScopeQuery?: string | null,
) => {
  const pq = emailPartitionAtSearch(partitionAt);
  const scopeSuffix =
    listScopeQuery != null && listScopeQuery.length > 0
      ? `${pq ? '&' : '?'}listScoped=true&${listScopeQuery.replace(/^\?/, '')}`
      : '';
  const key = id > 0 ? `/emails/${id}/thread${pq}${scopeSuffix}` : null;
  const { data, error, isLoading } = useSWR<EmailThreadResponse>(
    key,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
  );

  return {
    thread: data,
    isLoading,
    isError: error,
  };
};

export const useSearchFolders = () => {
  const { data, error, isLoading, mutate } = useSWR<EmailSearchFolder[]>(
    '/emails/search-folders',
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    }
  );

  const createSearchFolder = async (body: {
    name: string;
    search?: string;
    searchIn?: SearchInField[];
    accountId?: number;
    accountIds?: number[];
    folderKind?: SearchFolderKind;
    folder?: string;
    displayOrder?: number;
  }) => {
    const created = await api.post<EmailSearchFolder>('/emails/search-folders', body);
    await mutate((prev) => (prev ? [...prev, created.data] : [created.data]), false);
    return created.data;
  };

  const updateSearchFolder = async (
    id: number,
    body: Partial<{
      name: string;
      search: string;
      searchIn: SearchInField[];
      accountId: number | null;
      accountIds?: number[] | null;
      folderKind?: SearchFolderKind | null;
      folder: string | null;
      displayOrder: number;
    }>
  ) => {
    const updated = await api.patch<EmailSearchFolder>(`/emails/search-folders/${id}`, body);
    // Write updated folder into list cache so Edit uses the same object (no stale list item)
    await mutate(
      (prev) => (prev ? prev.map((f) => (f.id === id ? updated.data : f)) : [updated.data]),
      false
    );
    await mutate();
    return updated.data;
  };

  const deleteSearchFolder = async (id: number) => {
    await api.delete(`/emails/search-folders/${id}`);
    await mutate((prev) => (prev ? prev.filter((f) => f.id !== id) : []), false);
  };

  const shareSearchFolder = async (
    folderId: number,
    body: { sharedWithUserId: number; permission?: 'read' | 'write' }
  ) => {
    await api.post(`/emails/search-folders/${folderId}/share`, body);
    await mutate();
  };

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
    createSearchFolder,
    updateSearchFolder,
    deleteSearchFolder,
    shareSearchFolder,
  };
};

/** Fetch a single search folder by id (for edit form so we always show server data). */
export const useSearchFolder = (id: number | null) => {
  const { data, error, isLoading, mutate } = useSWR<EmailSearchFolder | null>(
    id != null ? `/emails/search-folders/${id}` : null,
    (url: string) => api.get(url).then((r) => r.data)
  );
  return { data: id != null ? data ?? null : null, isLoading, isError: error, mutate };
};

export const useEmailTags = () => {
  const { data, error, isLoading, mutate } = useSWR<EmailTag[]>(
    '/emails/tags',
    (url: string) => api.get(url).then((r) => r.data)
  );
  const createTag = async (name: string, color?: string | null) => {
    const created = await api.post<EmailTag>('/emails/tags', { name: name.trim(), color: color || undefined });
    await mutate((prev) => (prev ? [...prev, created.data] : [created.data]), false);
    return created.data;
  };
  const deleteTag = async (tagId: number) => {
    await api.delete(`/emails/tags/${tagId}`);
    await mutate((prev) => (prev ? prev.filter((t) => t.id !== tagId) : []), false);
  };
  const reorderTags = async (tagIds: number[]) => {
    const updated = await api.patch<EmailTag[]>('/emails/tags/reorder', { tagIds });
    await mutate(updated.data ?? [], false);
    return updated.data ?? [];
  };
  return { data: data ?? [], isLoading, isError: error, mutate, createTag, deleteTag, reorderTags };
};

export const usePersonalFolders = () => {
  const { data, error, isLoading, mutate } = useSWR<EmailPersonalFolderTreeNode[]>(
    '/emails/personal-folders',
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    }
  );

  const createPersonalFolder = async (body: {
    name: string;
    parentId?: number;
    displayOrder?: number;
  }) => {
    const created = await api.post<EmailPersonalFolderTreeNode>('/emails/personal-folders', body);
    await mutate();
    return created.data;
  };

  const updatePersonalFolder = async (
    id: number,
    body: Partial<{ name: string; parentId: number | null; displayOrder: number }>
  ) => {
    await api.patch(`/emails/personal-folders/${id}`, body);
    await mutate();
  };

  const deletePersonalFolder = async (id: number) => {
    await api.delete(`/emails/personal-folders/${id}`);
    await mutate();
  };

  const addEmailToFolder = async (folderId: number, emailId: number, partitionAt?: string | null) => {
    await api.post(
      `/emails/personal-folders/${folderId}/emails/${emailId}${emailPartitionAtSearch(partitionAt)}`
    );
    await mutate();
  };

  const removeEmailFromFolder = async (folderId: number, emailId: number, partitionAt?: string | null) => {
    await api.delete(
      `/emails/personal-folders/${folderId}/emails/${emailId}${emailPartitionAtSearch(partitionAt)}`
    );
    await mutate();
  };

  const reorderPersonalFolders = async (folderIds: number[]) => {
    await api.patch<EmailPersonalFolderTreeNode[]>('/emails/personal-folders/reorder', { folderIds });
    await mutate();
  };

  return {
    data: data ?? [],
    isLoading,
    isError: error,
    mutate,
    createPersonalFolder,
    updatePersonalFolder,
    deletePersonalFolder,
    addEmailToFolder,
    removeEmailFromFolder,
    reorderPersonalFolders,
  };
};

export const useSharedFolders = () => {
  const { data, error, isLoading, mutate } = useSWR<SharedFolderTreeNode[]>(
    '/emails/personal-folders/shared-with-me',
    (url: string) => api.get(url).then((r) => r.data)
  );

  const shareFolder = async (
    folderId: number,
    body: { sharedWithUserId: number; includeSubfolders?: boolean; permission?: 'read' | 'write' }
  ) => {
    await api.post(`/emails/personal-folders/${folderId}/share`, body);
    await mutate();
  };

  const revokeShare = async (shareId: number) => {
    await api.delete(`/emails/personal-folders/shares/${shareId}`);
    await mutate();
  };

  return {
    data: data ?? [],
    isLoading,
    isError: error,
    mutate,
    shareFolder,
    revokeShare,
  };
};

export interface SharedSearchFolderItem {
  shareId: number;
  folderId: number;
  name: string;
  search?: string | null;
  searchIn?: string[] | null;
  accountId?: number | null;
  accountIds?: number[];
  folderKind?: SearchFolderKind | string | null;
  folder?: string | null;
  displayOrder?: number;
  periodPreset?: string | null;
  periodYear?: number | null;
  ownerId: number;
  ownerName: string;
  ownerLastSyncAt?: string | null;
  ownerSyncStatus?: string | null;
  permission: 'read' | 'write';
}

export const useSharedSearchFolders = () => {
  const { data, error, isLoading, mutate } = useSWR<SharedSearchFolderItem[]>(
    '/emails/search-folders/shared-with-me',
    (url: string) => api.get(url).then((r) => r.data),
    { revalidateOnFocus: true }
  );
  const revokeShare = async (shareId: number) => {
    await api.delete(`/emails/search-folders/shares/${shareId}`);
    await mutate();
  };
  return { data: data ?? [], isLoading, isError: error, mutate, revokeShare };
};

export interface AccessibleAccount {
  id: number;
  emailAddress: string;
  displayName?: string | null;
  sidebarColor?: string | null;
  /** Backend: single_folder | full_mailbox */
  imapMirrorMode?: string;
  showInAccountsSidebar?: boolean;
  showSearchFolderInbox?: boolean;
  /** Show "Sent" (all) in /emails sidebar */
  showSentFolder?: boolean;
  /** Show "My sent" in /emails sidebar */
  showMySentFolder?: boolean;
  lastSyncAt?: string | null;
  syncStatus?: string | null;
}

export interface EmailImapFolder {
  id: number;
  emailAccountId: number;
  imapPath: string;
  displayName: string | null;
  parentPath: string | null;
  selectable: boolean;
  syncEnabled: boolean;
  presentOnServer: boolean;
  lastSyncedAt?: string | null;
  lastSyncError?: string | null;
}

/** IMAP folder trees for accounts (full_mailbox); keyed by account id */
export const useImapFoldersForAccounts = (accountIds: number[]) => {
  const key =
    accountIds.length > 0
      ? ['imap-folders', ...[...accountIds].sort((a, b) => a - b)]
      : null;
  const { data, error, isLoading, mutate } = useSWR<Record<number, EmailImapFolder[]>>(
    key,
    async () => {
      const results = await Promise.all(
        accountIds.map(async (id) => {
          const res = await api.get<EmailImapFolder[]>(`/emails/accounts/${id}/imap-folders`);
          return { id, folders: res.data ?? [] };
        }),
      );
      return Object.fromEntries(results.map((r) => [r.id, r.folders]));
    },
    { revalidateOnFocus: false },
  );
  return {
    data: data ?? {},
    isLoading,
    isError: error,
    mutate,
  };
};

export async function refreshImapFoldersFromServer(accountId: number): Promise<{ count: number }> {
  const res = await api.post<{ count: number }>(`/emails/accounts/${accountId}/imap-folders/refresh`);
  return res.data;
}

export interface SignatureOption {
  id: number;
  name: string;
  body: string | null;
  isDefault?: boolean;
}

export interface AccountWithSignatures extends AccessibleAccount {
  displayName?: string | null;
  signatures: SignatureOption[];
}

export const useAccessibleAccounts = () => {
  const { data, error, isLoading, mutate } = useSWR<AccessibleAccount[]>(
    '/emails/accounts/accessible',
    (url: string) => api.get(url).then((r) => r.data)
  );
  const updateAccountSidebarColor = async (accountId: number, color: string | null) => {
    await api.patch(`/emails/accounts/${accountId}/sidebar-color`, { color });
    await mutate();
  };
  return {
    data: data ?? [],
    isLoading,
    isError: error,
    mutate,
    updateAccountSidebarColor,
  };
};

export interface SignaturesResponse {
  byAccount: Record<
    number,
    { emailAddress: string; displayName?: string | null; signatures: SignatureOption[] }
  >;
  accounts: AccountWithSignatures[];
}

export const useSignatures = () => {
  const { data, error, isLoading, mutate } = useSWR<SignaturesResponse>(
    '/emails/signatures',
    (url: string) => api.get(url).then((r) => r.data)
  );
  return {
    data,
    accounts: data?.accounts ?? [],
    byAccount: data?.byAccount ?? {},
    isLoading,
    isError: error,
    mutate,
  };
};

export interface SendIdentityOption {
  id: number;
  name: string;
  displayName: string | null;
  emailAddress: string | null;
  replyTo: string | null;
  isDefault: boolean;
  displayOrder: number;
}

export interface SendIdentitiesResponse {
  byAccount: Record<
    number,
    { emailAddress: string; displayName?: string | null; identities: SendIdentityOption[] }
  >;
  accounts: Array<{ id: number; emailAddress: string; displayName?: string | null; identities: SendIdentityOption[] }>;
  identities: SendIdentityOption[];
}

export const useSendIdentities = () => {
  const { data, error, isLoading, mutate } = useSWR<SendIdentitiesResponse>(
    '/emails/send-identities/list',
    (url: string) => api.get(url).then((r) => r.data)
  );
  return {
    data,
    byAccount: data?.byAccount ?? {},
    accounts: data?.accounts ?? [],
    identities: data?.identities ?? [],
    isLoading,
    isError: error,
    mutate,
  };
};

export interface RecentRecipientItem {
  id: number;
  email: string;
  name?: string | null;
  lastUsedAt: string;
}

export const useRecentRecipients = () => {
  const { data, error, isLoading, mutate } = useSWR<RecentRecipientItem[]>(
    '/emails/recent-recipients',
    (url: string) => api.get(url).then((r) => r.data)
  );
  const clearSentHistory = async (): Promise<number> => {
    const res = await api.delete<{ deleted: number }>('/emails/recent-recipients');
    await mutate([], { revalidate: false });
    return res.data?.deleted ?? 0;
  };
  return {
    data: data ?? [],
    isLoading,
    isError: error,
    mutate,
    clearSentHistory,
  };
};

export interface SharedAccountItem {
  shareId: number;
  accountId: number;
  emailAddress: string;
  displayName?: string | null;
  ownerId: number;
  ownerName: string;
  permission: 'read' | 'write';
  emailCount?: number;
  showSentFolder?: boolean;
  showMySentFolder?: boolean;
  showInAccountsSidebar?: boolean;
}

export const useSharedAccounts = () => {
  const { data, error, isLoading, mutate } = useSWR<SharedAccountItem[]>(
    '/emails/accounts/shared-with-me',
    (url: string) => api.get(url).then((r) => r.data)
  );
  const revokeShare = async (shareId: number) => {
    await api.delete(`/emails/accounts/shares/${shareId}`);
    await mutate();
  };
  return { data: data ?? [], isLoading, isError: error, mutate, revokeShare };
};

export type AccountMailboxFolder = 'INBOX' | 'DRAFT' | 'SENT' | 'MY_SENT' | 'JUNK';

export interface UnreadCounts {
  all: number;
  allTotal: number;
  /** Per account: unread in INBOX (single_folder) or sum across all folders (full_mailbox) */
  byAccount: Record<string, number>;
  byAccountTotal: Record<string, number>;
  /** Keys are `${accountId}:${folder}` for INBOX, DRAFT, SENT, MY_SENT */
  byAccountFolder: Record<string, number>;
  byAccountFolderTotal: Record<string, number>;
  /** full_mailbox: per-account map of IMAP path (Email.folder) → unread count */
  byAccountImapFolder: Record<string, Record<string, number>>;
  byAccountImapFolderTotal: Record<string, Record<string, number>>;
  /** Keys are search folder IDs (number or string from JSON) */
  bySearchFolder: Record<string, number>;
  bySearchFolderTotal: Record<string, number>;
  /** Keys are personal folder IDs (number or string from JSON) */
  byPersonalFolder: Record<string, number>;
}

function normalizeUnreadCounts(raw: unknown): UnreadCounts {
  const obj = raw != null && typeof raw === 'object' && 'data' in raw
    ? (raw as { data?: unknown }).data
    : raw;
  if (obj == null || typeof obj !== 'object') {
    return {
      all: 0,
      allTotal: 0,
      byAccount: {},
      byAccountTotal: {},
      byAccountFolder: {},
      byAccountFolderTotal: {},
      byAccountImapFolder: {},
      byAccountImapFolderTotal: {},
      bySearchFolder: {},
      bySearchFolderTotal: {},
      byPersonalFolder: {},
    };
  }
  const o = obj as Record<string, unknown>;
  const all = typeof o.all === 'number' ? o.all : 0;
  const allTotal = typeof o.allTotal === 'number' ? o.allTotal : 0;
  const byAccount: Record<string, number> = {};
  if (o.byAccount != null && typeof o.byAccount === 'object' && !Array.isArray(o.byAccount)) {
    for (const [k, v] of Object.entries(o.byAccount)) {
      if (typeof v === 'number') byAccount[String(k)] = v;
    }
  }
  const byAccountTotal: Record<string, number> = {};
  if (o.byAccountTotal != null && typeof o.byAccountTotal === 'object' && !Array.isArray(o.byAccountTotal)) {
    for (const [k, v] of Object.entries(o.byAccountTotal)) {
      if (typeof v === 'number') byAccountTotal[String(k)] = v;
    }
  }
  const byAccountFolder: Record<string, number> = {};
  if (o.byAccountFolder != null && typeof o.byAccountFolder === 'object' && !Array.isArray(o.byAccountFolder)) {
    for (const [k, v] of Object.entries(o.byAccountFolder)) {
      if (typeof v === 'number') byAccountFolder[String(k)] = v;
    }
  }
  const byAccountFolderTotal: Record<string, number> = {};
  if (o.byAccountFolderTotal != null && typeof o.byAccountFolderTotal === 'object' && !Array.isArray(o.byAccountFolderTotal)) {
    for (const [k, v] of Object.entries(o.byAccountFolderTotal)) {
      if (typeof v === 'number') byAccountFolderTotal[String(k)] = v;
    }
  }
  const byAccountImapFolder: Record<string, Record<string, number>> = {};
  if (o.byAccountImapFolder != null && typeof o.byAccountImapFolder === 'object' && !Array.isArray(o.byAccountImapFolder)) {
    for (const [aid, inner] of Object.entries(o.byAccountImapFolder)) {
      if (inner == null || typeof inner !== 'object' || Array.isArray(inner)) continue;
      const m: Record<string, number> = {};
      for (const [path, n] of Object.entries(inner)) {
        if (typeof n === 'number') m[path] = n;
      }
      if (Object.keys(m).length > 0) byAccountImapFolder[String(aid)] = m;
    }
  }
  const byAccountImapFolderTotal: Record<string, Record<string, number>> = {};
  if (
    o.byAccountImapFolderTotal != null &&
    typeof o.byAccountImapFolderTotal === 'object' &&
    !Array.isArray(o.byAccountImapFolderTotal)
  ) {
    for (const [aid, inner] of Object.entries(o.byAccountImapFolderTotal)) {
      if (inner == null || typeof inner !== 'object' || Array.isArray(inner)) continue;
      const m: Record<string, number> = {};
      for (const [path, n] of Object.entries(inner)) {
        if (typeof n === 'number') m[path] = n;
      }
      if (Object.keys(m).length > 0) byAccountImapFolderTotal[String(aid)] = m;
    }
  }
  const bySearchFolder: Record<string, number> = {};
  if (o.bySearchFolder != null && typeof o.bySearchFolder === 'object' && !Array.isArray(o.bySearchFolder)) {
    for (const [k, v] of Object.entries(o.bySearchFolder)) {
      if (typeof v === 'number') bySearchFolder[String(k)] = v;
    }
  }
  const bySearchFolderTotal: Record<string, number> = {};
  if (o.bySearchFolderTotal != null && typeof o.bySearchFolderTotal === 'object' && !Array.isArray(o.bySearchFolderTotal)) {
    for (const [k, v] of Object.entries(o.bySearchFolderTotal)) {
      if (typeof v === 'number') bySearchFolderTotal[String(k)] = v;
    }
  }
  const byPersonalFolder: Record<string, number> = {};
  if (o.byPersonalFolder != null && typeof o.byPersonalFolder === 'object' && !Array.isArray(o.byPersonalFolder)) {
    for (const [k, v] of Object.entries(o.byPersonalFolder)) {
      if (typeof v === 'number') byPersonalFolder[String(k)] = v;
    }
  }
  return {
    all,
    allTotal,
    byAccount,
    byAccountTotal,
    byAccountFolder,
    byAccountFolderTotal,
    byAccountImapFolder,
    byAccountImapFolderTotal,
    bySearchFolder,
    bySearchFolderTotal,
    byPersonalFolder,
  };
}

export async function markEmailMentionsRead(emailId: number): Promise<number> {
  const response = await api.post<{ updated: number }>(`/emails/${emailId}/mentions/mark-read`);
  return response.data?.updated ?? 0;
}

export async function markAllMentionsRead(): Promise<number> {
  const response = await api.post<{ updated: number }>('/emails/mentions/mark-all-read');
  return response.data?.updated ?? 0;
}

/** Folder-scope filters for POST /emails/mark-folder-read (no UI quick-filters). */
export interface MarkFolderAsReadFilters {
  accountId?: number;
  folder?: string;
  sentByMe?: boolean;
  isArchived?: boolean;
  mentionedMe?: boolean;
  personalFolderId?: number;
  shareId?: number;
  searchFolderId?: number;
  sharedSearchFolderShareId?: number;
  baseSearch?: string;
  baseSearchIn?: string;
  baseAccountId?: number;
  baseAccountIds?: string;
  baseFolder?: string;
  sharedOnly?: boolean;
  tagId?: number;
}

export interface MarkFolderAsReadBatchResult {
  marked: number;
  totalUnread: number;
  nextCursor: string | null;
  done: boolean;
}

export async function markFolderAsReadBatch(
  body: MarkFolderAsReadFilters & { take?: number; finalize?: boolean },
): Promise<MarkFolderAsReadBatchResult> {
  const response = await api.post<MarkFolderAsReadBatchResult>('/emails/mark-folder-read', body);
  return response.data;
}

export function useMentionUnreadCount() {
  const pageVisible = usePageVisible();
  const { data, error, isLoading, mutate } = useSWR<{ count: number }>(
    '/emails/mentions/unread-count',
    async (url: string) => {
      const response = await api.get(url);
      return response.data as { count: number };
    },
    { refreshInterval: pageVisible ? 60_000 : 0 },
  );

  return {
    count: data?.count ?? 0,
    isLoading,
    isError: error,
    mutate,
  };
}

export function revalidateMentionCaches(
  globalMutate: (
    matcher: (key: unknown) => boolean,
    data?: unknown,
    opts?: { revalidate?: boolean },
  ) => Promise<unknown[]>,
) {
  return globalMutate(
    (key) =>
      typeof key === 'string' &&
      (key.includes('mentions/unread-count') ||
        key.includes('mentionedMe=true') ||
        (key.startsWith('/emails?') && key.includes('mentionedMe'))),
    undefined,
    { revalidate: true },
  );
}

export const useUnreadCounts = (sharedOnly?: boolean) => {
  const url = sharedOnly ? '/emails/unread-counts?sharedOnly=true' : '/emails/unread-counts';
  const { data, error, isLoading, mutate } = useSWR<UnreadCounts>(
    url,
    async (url: string) => {
      const response = await api.get(url);
      return normalizeUnreadCounts(response.data);
    }
  );

  return {
    data: data ?? {
      all: 0,
      allTotal: 0,
      byAccount: {},
      byAccountTotal: {},
      byAccountFolder: {},
      byAccountFolderTotal: {},
      byAccountImapFolder: {},
      byAccountImapFolderTotal: {},
      bySearchFolder: {},
      bySearchFolderTotal: {},
      byPersonalFolder: {},
    },
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSearchFolderUnreadCounts = (sharedOnly?: boolean) => {
  const url = sharedOnly
    ? '/emails/unread-counts/search-folders?sharedOnly=true'
    : '/emails/unread-counts/search-folders';
  const { data, error, isLoading, mutate } = useSWR<{
    bySearchFolder: Record<string, number>;
    bySearchFolderTotal: Record<string, number>;
  }>(
    url,
    async (fetchUrl: string) => {
      const response = await api.get(fetchUrl);
      const raw = response.data as {
        bySearchFolder?: Record<string, number>;
        bySearchFolderTotal?: Record<string, number>;
      };
      return {
        bySearchFolder: raw.bySearchFolder ?? {},
        bySearchFolderTotal: raw.bySearchFolderTotal ?? {},
      };
    },
    { revalidateOnFocus: false },
  );

  return {
    data: data ?? { bySearchFolder: {}, bySearchFolderTotal: {} },
    isLoading,
    isError: error,
    mutate,
  };
};

/** Draft from API: to/cc/bcc are arrays of { name?, email } */
export interface EmailDraft {
  id: number;
  userId: number;
  emailAccountId: number;
  to: Array<{ name?: string; email: string }>;
  cc?: Array<{ name?: string; email: string }> | null;
  bcc?: Array<{ name?: string; email: string }> | null;
  subject: string;
  bodyHtml?: string | null;
  bodyText?: string | null;
  bodyKind?: 'plain' | 'rich' | null;
  composeState?: import('./composeBody/types').ComposeBodyDocument | null;
  updatedAt: string;
  /** True when the draft belongs to the current user. */
  isOwner?: boolean;
  /** Author display name (useful when viewing shared account drafts). */
  ownerName?: string;
}

export interface EmailTemplateFolder {
  id: number;
  name: string;
  parentId: number | null;
  displayOrder: number;
  scope?: 'PERSONAL' | 'COMPANY';
  isSystemRoot?: boolean;
  isLocked?: boolean;
  isOwner?: boolean;
  canEdit?: boolean;
}

export interface SharedEmailTemplateFolder {
  shareId: number;
  folderId: number;
  name: string;
  parentId: number | null;
  displayOrder: number;
  ownerName: string;
  includeSubfolders: boolean;
  permission: 'read' | 'write';
  isOwner: false;
}

/** Template from API */
export interface EmailTemplateAttachment {
  id: number;
  name: string;
  mimeType: string;
  sizeBytes: number;
  displayOrder?: number;
}

/** Template from API */
export interface EmailTemplate {
  id: number;
  userId: number;
  emailAccountId?: number | null;
  folderId?: number | null;
  name: string;
  to: Array<{ name?: string; email: string }>;
  cc?: Array<{ name?: string; email: string }> | null;
  bcc?: Array<{ name?: string; email: string }> | null;
  subject: string;
  bodyHtml?: string | null;
  bodyText?: string | null;
  bodyKind?: 'plain' | 'rich';
  composeState?: import('./composeBody/types').ComposeBodyDocument | null;
  createdAt: string;
  updatedAt: string;
  emailAccount?: { id: number; emailAddress: string } | null;
  folder?: { id: number; name: string; parentId: number | null } | null;
  isOwner?: boolean;
  ownerName?: string;
  shareId?: number;
  permission?: 'read' | 'write';
  scope?: 'PERSONAL' | 'COMPANY';
  attachments?: EmailTemplateAttachment[];
}

export interface EmailTemplateDocument {
  id: number;
  folderId: number;
  name: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  scope?: 'PERSONAL' | 'COMPANY';
}

export interface EmailTemplateLibrarySection {
  folders: EmailTemplateFolder[];
  templates: EmailTemplate[];
  documents: EmailTemplateDocument[];
}

export interface EmailTemplateLibrary {
  company: EmailTemplateLibrarySection & { canEdit: boolean };
  personal: EmailTemplateLibrarySection;
  shared: {
    folders: SharedEmailTemplateFolder[];
    templates: EmailTemplate[];
  };
}

export interface TemplateImportResult {
  foldersCreated: number;
  templatesCreated: number;
  documentsCreated: number;
  skipped: number;
  errors: string[];
}

/** Draft list item with account info for Drafts view */
export interface EmailDraftListItem extends EmailDraft {
  emailAccount?: {
    id: number;
    emailAddress: string;
    displayName?: string | null;
  } | null;
}

function formatAddressesToComma(arr: Array<{ name?: string; email: string }> | null | undefined): string {
  if (!arr || !Array.isArray(arr)) return '';
  return arr.map((a) => (a.name && a.email ? `${a.name} <${a.email}>` : a.email || '')).filter(Boolean).join(', ');
}

/** Load draft for an account (for restore prompt or prefill). Returns null if no draft. */
export const useDraft = (draftId: number | undefined) => {
  const { data, mutate } = useSWR<EmailDraft | null>(
    draftId != null ? [`/emails/drafts`, draftId] : null,
    async ([_, id]: [string, number]) => {
      const res = await api.get(`/emails/drafts?draftId=${id}`);
      return res.data ?? null;
    }
  );
  return { draft: data ?? null, mutateDraft: mutate };
};

/** List all drafts for the current user (across accounts) for the Drafts view. */
export const useDraftsList = () => {
  const { data, error, isLoading, mutate } = useSWR<EmailDraftListItem[]>(
    '/emails/drafts/list',
    (url: string) => api.get(url).then((r) => r.data),
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
};

export type EmailOutboxStatus = 'queued' | 'sending' | 'sent' | 'cancelled' | 'failed';

export type EmailOutboxRecipients = {
  to?: string[];
  cc?: string[];
  bcc?: string[];
};

export type EmailOutboxPayload = {
  accountId?: number;
  sendIdentityId?: number;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  text?: string;
  html?: string;
  attachments?: Array<{
    filename: string;
    path?: string;
    content?: string;
    contentType?: string;
  }>;
};

export type EmailOutboxListItem = {
  id: number;
  emailAccountId: number;
  status: EmailOutboxStatus;
  subject: string | null;
  recipients: EmailOutboxRecipients;
  scheduledSendAt: string | null;
  sentAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  emailAccount?: {
    id: number;
    emailAddress: string;
    displayName: string | null;
  };
};

export type EmailOutboxDetail = EmailOutboxListItem & {
  payload: EmailOutboxPayload | null;
};

/** Queued, sending, and failed outbound emails for the Outbox view. */
export function outboxListNeedsPolling(items: EmailOutboxListItem[]): boolean {
  return items.some((i) => i.status === 'queued' || i.status === 'sending');
}

export const useOutboxList = () => {
  const pageVisible = usePageVisible();
  const { data, error, isLoading, mutate } = useSWR<EmailOutboxListItem[]>(
    '/emails/outbox',
    (url: string) => api.get(url).then((r) => r.data),
    {
      revalidateOnFocus: true,
      dedupingInterval: 1500,
    },
  );

  const items = data ?? [];
  const needsPoll = pageVisible && outboxListNeedsPolling(items);

  React.useEffect(() => {
    if (!needsPoll) return;
    const timer = window.setInterval(() => {
      void mutate();
    }, 2000);
    return () => window.clearInterval(timer);
  }, [needsPoll, mutate]);

  return { data: items, isLoading, isError: error, mutate };
};

export async function revalidateOutboxCaches(): Promise<void> {
  const { mutate } = await import('swr');
  await mutate('/emails/outbox');
}

export async function cancelOutboxApi(outboxId: number): Promise<void> {
  await api.post(`/emails/outbox/${outboxId}/cancel`);
}

export async function resendOutboxApi(
  outboxId: number,
): Promise<{ undoDelaySeconds: number; scheduledSendAt: string | null }> {
  const res = await api.post<{ undoDelaySeconds: number; scheduledSendAt: string | null }>(
    `/emails/outbox/${outboxId}/retry`,
  );
  return res.data;
}

export async function dismissOutboxApi(outboxId: number): Promise<void> {
  await api.post(`/emails/outbox/${outboxId}/dismiss`);
}

export async function fetchOutboxApi(outboxId: number): Promise<EmailOutboxDetail> {
  const res = await api.get<EmailOutboxDetail>(`/emails/outbox/${outboxId}`);
  return res.data;
}

/** Load a saved draft by id (null when none). */
export async function fetchDraftApi(draftId: number): Promise<EmailDraft | null> {
  const res = await api.get<EmailDraft | null>(`/emails/drafts?draftId=${draftId}`);
  return res.data ?? null;
}

/** Save draft to API. Creates a new draft when draftId is omitted; updates when draftId is set. */
export const saveDraftApi = async (body: {
  draftId?: number;
  accountId: number;
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  html?: string;
  text?: string;
  bodyKind?: 'plain' | 'rich';
  composeState?: import('./composeBody/types').ComposeBodyDocument;
}) => {
  const res = await api.post<EmailDraft>('/emails/drafts', {
    ...body,
    to: body.to ?? '',
    cc: body.cc ?? '',
    bcc: body.bcc ?? '',
  });
  return res.data;
};

/** Delete a saved draft by id (no-op when it does not exist). */
export async function deleteDraftApi(draftId: number): Promise<boolean> {
  const res = await api.delete<{ deleted: boolean }>(`/emails/drafts?draftId=${draftId}`);
  return Boolean(res.data?.deleted);
}

export async function revalidateEmailDraftCaches(draftId?: number): Promise<void> {
  const { mutate } = await import('swr');
  await Promise.all([
    mutate('/emails/drafts/list'),
    draftId != null ? mutate(['/emails/drafts', draftId]) : Promise.resolve(),
  ]);
}

/** List templates. */
export const useEmailTemplates = () => {
  const { data, error, isLoading, mutate } = useSWR<EmailTemplate[]>(
    '/emails/templates',
    (url: string) => api.get(url).then((r) => r.data)
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
};

/** Folders + templates for the Templates library view. */
export const useEmailTemplateLibrary = () => {
  const { data, error, isLoading, mutate } = useSWR<EmailTemplateLibrary>(
    '/emails/templates/library',
    (url: string) => api.get(url).then((r) => r.data)
  );
  const emptySection: EmailTemplateLibrarySection = { folders: [], templates: [], documents: [] };
  return {
    company: data?.company ?? { ...emptySection, canEdit: false },
    personal: data?.personal ?? emptySection,
    shared: data?.shared ?? { folders: [], templates: [] },
    isLoading,
    isError: error,
    mutate,
  };
};

export async function createTemplateFolderApi(body: {
  name: string;
  scope: 'PERSONAL' | 'COMPANY';
  parentId?: number;
}) {
  const res = await api.post<EmailTemplateFolder>('/emails/template-folders', body);
  return res.data;
}

export async function updateTemplateFolderApi(
  folderId: number,
  body: { name?: string; parentId?: number | null; isLocked?: boolean },
) {
  const res = await api.patch<EmailTemplateFolder>(`/emails/template-folders/${folderId}`, body);
  return res.data;
}

export async function deleteTemplateFolderApi(folderId: number) {
  const res = await api.delete<{
    foldersDeleted: number;
    templatesDeleted: number;
    documentsDeleted: number;
  }>(`/emails/template-folders/${folderId}`);
  return res.data;
}

export type TemplateFolderCopyResult = {
  rootFolderId: number;
  foldersCopied: number;
  templatesCopied: number;
  documentsCopied: number;
};

export async function copyTemplateFolderApi(
  folderId: number,
  body: { targetParentId: number; newName?: string },
) {
  const res = await api.post<TemplateFolderCopyResult>(`/emails/template-folders/${folderId}/copy`, body);
  return res.data;
}

export async function deleteTemplateApi(templateId: number) {
  await api.delete(`/emails/templates/${templateId}`);
}

export async function getTemplateApi(templateId: number): Promise<EmailTemplate> {
  const res = await api.get<EmailTemplate>(`/emails/templates/${templateId}`);
  return res.data;
}

export async function fetchTemplateAttachmentFile(
  templateId: number,
  attachment: EmailTemplateAttachment,
): Promise<File> {
  const res = await api.get(`/emails/templates/${templateId}/attachments/${attachment.id}/download`, {
    responseType: 'blob',
  });
  return new File([res.data], attachment.name, { type: attachment.mimeType });
}

export async function templateDocumentToFile(doc: EmailTemplateDocument): Promise<File> {
  const res = await api.get(`/emails/template-documents/${doc.id}/download`, { responseType: 'blob' });
  return new File([res.data], doc.name, { type: doc.mimeType });
}

export type ParsedComposeMessage = {
  subject: string;
  to: Array<{ name?: string; email: string }>;
  cc?: Array<{ name?: string; email: string }>;
  bcc?: Array<{ name?: string; email: string }>;
  html?: string | null;
  text?: string | null;
  bodyKind?: 'plain' | 'rich';
  attachments?: Array<{
    filename: string;
    contentType: string;
    content: string;
  }>;
};

export async function importComposeMessage(file: File): Promise<ParsedComposeMessage> {
  const formData = new FormData();
  formData.append('file', file, file.name || 'message.eml');
  const res = await api.post<ParsedComposeMessage>('/emails/compose/import', formData, {
    transformRequest: [
      (data, headers) => {
        if (headers && typeof headers === 'object') {
          delete (headers as Record<string, unknown>)['Content-Type'];
          delete (headers as Record<string, unknown>)['content-type'];
        }
        return data;
      },
    ],
  });
  return res.data;
}

export type ReimportEmlResult = {
  found: true;
  matchedBy: 'emailId' | 'messageId';
  id: number;
  partitionAt: string;
  messageId: string;
  subject: string | null;
  folder: string;
  emailAccountId: number;
  attachmentCount: number;
  changes: string[];
  fields: {
    subject: { before: string | null; after: string | null; changed: boolean };
    from: { before: string; after: string; changed: boolean };
    to: { before: string; after: string; changed: boolean };
    bodyHtml: { beforeLength: number; afterLength: number; changed: boolean };
    bodyText: { beforeLength: number; afterLength: number; changed: boolean };
    messageHeaders: { beforeLength: number; afterLength: number; changed: boolean };
  };
  attachments: {
    beforeCount: number;
    afterCount: number;
    before: Array<{ filename: string; size: number; contentType: string }>;
    after: Array<{ filename: string; size: number; contentType: string }>;
    added: string[];
    removed: string[];
    sizeChanged: string[];
  };
  preserved: string[];
};

/** Admin-only: upload .eml and refresh matching email MIME/attachments by Message-ID. */
export async function reimportEmailFromEml(
  file: File,
  opts?: { emailId?: number; partitionAt?: string | null },
): Promise<ReimportEmlResult> {
  const formData = new FormData();
  formData.append('file', file, file.name || 'message.eml');
  const params = new URLSearchParams();
  if (opts?.emailId != null) params.set('emailId', String(opts.emailId));
  if (opts?.partitionAt) params.set('partitionAt', opts.partitionAt);
  const qs = params.toString();
  const res = await api.post<ReimportEmlResult>(
    `/emails/admin/reimport-eml${qs ? `?${qs}` : ''}`,
    formData,
    {
      transformRequest: [
        (data, headers) => {
          if (headers && typeof headers === 'object') {
            delete (headers as Record<string, unknown>)['Content-Type'];
            delete (headers as Record<string, unknown>)['content-type'];
          }
          return data;
        },
      ],
    },
  );
  return res.data;
}

export interface TemplateImportUploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export interface TemplateFolderImportBatchProgress {
  phase: 'uploading' | 'processing';
  batchIndex: number;
  batchCount: number;
  filesTotal: number;
  filesCompleted: number;
  filesInBatch: number;
  bytesTotal: number;
  /** Bytes fully uploaded across completed batches + current batch upload. */
  bytesUploaded: number;
  /** 0–100 overall estimate for the whole folder import. */
  overallPercent: number;
  /** Relative path of the file currently being uploaded (estimated from batch byte progress). */
  latestPath?: string;
  /** 1-based index of the file currently uploading across the whole folder. */
  currentFileIndex?: number;
}

export interface TemplateFolderImportOptions {
  onUploadProgress?: (progress: TemplateImportUploadProgress) => void;
  onBatchProgress?: (progress: TemplateFolderImportBatchProgress) => void;
  /**
   * When true (import into Personal/Company library root), keep the picked folder
   * name as the first path segment so it becomes a child under the root.
   */
  preserveTopLevelFolder?: boolean;
}

function emptyImportResult(): TemplateImportResult {
  return {
    foldersCreated: 0,
    templatesCreated: 0,
    documentsCreated: 0,
    skipped: 0,
    errors: [],
  };
}

export type TemplateImportSessionStatus =
  | 'UPLOADING'
  | 'QUEUED'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

export type TemplateImportSessionDto = {
  id: string;
  folderId: number;
  status: TemplateImportSessionStatus;
  fileCount: number;
  bytesTotal: number;
  processedCount: number;
  result: TemplateImportResult | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
};

async function createTemplateImportSession(folderId: number): Promise<TemplateImportSessionDto> {
  const res = await api.post<TemplateImportSessionDto>('/emails/templates/import/session', {
    folderId,
  });
  return res.data;
}

async function uploadTemplateImportSessionBatch(
  sessionId: string,
  items: Array<{ file: File; path: string }>,
  onUploadProgress?: (progress: TemplateImportUploadProgress) => void,
): Promise<TemplateImportSessionDto> {
  const formData = new FormData();
  const paths: string[] = [];
  for (const item of items) {
    formData.append('files', item.file);
    paths.push(item.path);
  }
  formData.append('paths', JSON.stringify(paths));
  const res = await api.post<TemplateImportSessionDto>(
    `/emails/templates/import/session/${sessionId}/files`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Upload-only: keep shorter than full import timeout
      timeout: APP_CONFIG.api.folderImportTimeout,
      onUploadProgress: (event) => {
        if (!onUploadProgress) return;
        const total = event.total ?? 0;
        const loaded = event.loaded ?? 0;
        const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
        onUploadProgress({ loaded, total, percent });
      },
    },
  );
  return res.data;
}

async function commitTemplateImportSession(sessionId: string): Promise<TemplateImportSessionDto> {
  const res = await api.post<TemplateImportSessionDto>(
    `/emails/templates/import/session/${sessionId}/commit`,
  );
  return res.data;
}

async function getTemplateImportSession(sessionId: string): Promise<TemplateImportSessionDto> {
  const res = await api.get<TemplateImportSessionDto>(
    `/emails/templates/import/session/${sessionId}`,
  );
  return res.data;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Import a folder by staging files on the server, then polling a background import job.
 */
export async function importTemplatesFolder(
  files: File[],
  folderId: number,
  options?: TemplateFolderImportOptions,
): Promise<TemplateImportResult> {
  const items = buildFolderImportItems(files, {
    stripCommonRoot: !options?.preserveTopLevelFolder,
  });
  if (items.length === 0) {
    return emptyImportResult();
  }

  const batches = chunkFolderImportItems(items);
  const bytesTotal = items.reduce((sum, item) => sum + item.file.size, 0);
  let bytesCompleted = 0;
  let filesUploaded = 0;

  /** Map multipart byte progress onto a file path within the current batch. */
  const estimateBatchFile = (
    batchItems: Array<{ file: File; path: string }>,
    loadedInRequest: number,
    requestTotal: number,
  ): { path: string; indexInBatch: number } | undefined => {
    if (batchItems.length === 0) return undefined;
    const batchBytes = batchItems.reduce((sum, item) => sum + item.file.size, 0);
    // Multipart overhead: scale loaded bytes toward payload size when totals differ.
    const scaledLoaded =
      requestTotal > 0 && batchBytes > 0
        ? Math.min(batchBytes, (loadedInRequest / requestTotal) * batchBytes)
        : Math.min(batchBytes, loadedInRequest);

    let remaining = scaledLoaded;
    for (let i = 0; i < batchItems.length; i++) {
      const size = Math.max(1, batchItems[i].file.size);
      if (remaining <= size || i === batchItems.length - 1) {
        return { path: batchItems[i].path, indexInBatch: i };
      }
      remaining -= size;
    }
    const last = batchItems[batchItems.length - 1];
    return { path: last.path, indexInBatch: batchItems.length - 1 };
  };

  const emitUpload = (
    batchIndex: number,
    withinBatch: number,
    latest?: { path: string; indexInBatch: number },
  ) => {
    const batch = batches[batchIndex];
    const clamped = Math.min(1, Math.max(0, withinBatch));
    const bytesUploaded = bytesCompleted + clamped * batch.bytes;
    // Upload phase occupies 0–70% of the overall bar; processing uses 70–99%.
    const uploadFraction = (batchIndex + clamped) / batches.length;
    const overallPercent = Math.min(70, Math.round(uploadFraction * 70));
    const fileHint =
      latest ??
      (clamped <= 0
        ? { path: batch.items[0]?.path ?? '', indexInBatch: 0 }
        : { path: batch.items[batch.items.length - 1]?.path ?? '', indexInBatch: batch.items.length - 1 });
    const currentFileIndex =
      fileHint.path && batch.items.length > 0
        ? filesUploaded + fileHint.indexInBatch + 1
        : undefined;

    options?.onBatchProgress?.({
      phase: 'uploading',
      batchIndex,
      batchCount: batches.length,
      filesTotal: items.length,
      filesCompleted: filesUploaded,
      filesInBatch: batch.items.length,
      bytesTotal,
      bytesUploaded,
      overallPercent,
      latestPath: fileHint.path || undefined,
      currentFileIndex,
    });

    options?.onUploadProgress?.({
      loaded: bytesUploaded,
      total: bytesTotal,
      percent: bytesTotal > 0 ? Math.round((bytesUploaded / bytesTotal) * 100) : 0,
    });
  };

  const emitProcessing = (processedCount: number, fileCount: number) => {
    const fraction = fileCount > 0 ? Math.min(1, processedCount / fileCount) : 0;
    const overallPercent = Math.min(99, Math.round(70 + fraction * 29));
    options?.onBatchProgress?.({
      phase: 'processing',
      batchIndex: Math.max(0, batches.length - 1),
      batchCount: batches.length,
      filesTotal: items.length,
      filesCompleted: processedCount,
      filesInBatch: items.length,
      bytesTotal,
      bytesUploaded: bytesTotal,
      overallPercent,
    });
  };

  const session = await createTemplateImportSession(folderId);

  try {
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      emitUpload(batchIndex, 0);
      await uploadTemplateImportSessionBatch(session.id, batch.items, ({ loaded, total }) => {
        const fraction = total > 0 ? Math.min(1, loaded / total) : 0;
        const latest = estimateBatchFile(batch.items, loaded, total || batch.bytes);
        emitUpload(batchIndex, fraction, latest);
      });
      bytesCompleted += batch.bytes;
      filesUploaded += batch.items.length;
      emitUpload(batchIndex, 1, {
        path: batch.items[batch.items.length - 1]?.path ?? '',
        indexInBatch: Math.max(0, batch.items.length - 1),
      });
    }

    emitProcessing(0, items.length);
    await commitTemplateImportSession(session.id);

    const pollStarted = Date.now();
    const pollDeadline = pollStarted + APP_CONFIG.api.folderImportTimeout;
    while (Date.now() < pollDeadline) {
      const status = await getTemplateImportSession(session.id);
      emitProcessing(status.processedCount, Math.max(status.fileCount, items.length));

      if (status.status === 'COMPLETED') {
        return status.result ?? emptyImportResult();
      }
      if (status.status === 'FAILED' || status.status === 'CANCELLED') {
        const result = status.result ?? emptyImportResult();
        if (status.errorMessage) {
          result.errors = [...result.errors, status.errorMessage];
        }
        if (result.errors.length === 0 && status.errorMessage) {
          throw new Error(status.errorMessage);
        }
        if (status.errorMessage && result.templatesCreated + result.documentsCreated === 0) {
          throw new Error(status.errorMessage);
        }
        return result;
      }

      await sleep(1500);
    }

    throw new Error(
      'Import is still running on the server but the wait timed out. Refresh the library — files may appear shortly.',
    );
  } catch (err) {
    try {
      const latest = await getTemplateImportSession(session.id);
      if (latest.status === 'UPLOADING') {
        await api.delete(`/emails/templates/import/session/${session.id}`);
      }
    } catch {
      /* ignore cancel failures during error cleanup */
    }
    throw err;
  }
}


export type UploadTemplateDocumentOptions = {
  ifExists?: 'auto-rename' | 'overwrite' | 'skip' | 'fail';
  name?: string;
};

export type UploadTemplateDocumentResult =
  | EmailTemplateDocument
  | { skipped: true };

export async function uploadTemplateDocumentApi(
  folderId: number,
  file: File,
  options: UploadTemplateDocumentOptions = {},
): Promise<UploadTemplateDocumentResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folderId', String(folderId));
  if (options.ifExists) {
    formData.append('ifExists', options.ifExists);
  }
  if (options.name?.trim()) {
    formData.append('name', options.name.trim());
  }
  const res = await api.post<EmailTemplateDocument | { skipped: true }>(
    '/emails/template-documents',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return res.data;
}

export async function downloadTemplateDocumentApi(documentId: number, filename: string): Promise<void> {
  const res = await api.get(`/emails/template-documents/${documentId}/download`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function deleteTemplateDocumentApi(documentId: number): Promise<void> {
  await api.delete(`/emails/template-documents/${documentId}`);
}

/** Save template to API. */
export const saveTemplateApi = async (body: {
  name: string;
  accountId?: number;
  folderId: number;
  scope?: 'PERSONAL' | 'COMPANY';
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  html?: string;
  text?: string;
  bodyKind?: 'plain' | 'rich';
  composeState?: import('./composeBody/types').ComposeBodyDocument;
}) => {
  const res = await api.post<EmailTemplate>('/emails/templates', body);
  return res.data;
};

/** Update an existing library template in place. */
export async function updateTemplateApi(
  templateId: number,
  body: {
    name: string;
    accountId?: number;
    folderId: number;
    to?: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    html?: string;
    text?: string;
    bodyKind?: 'plain' | 'rich';
    composeState?: import('./composeBody/types').ComposeBodyDocument;
  },
): Promise<EmailTemplate> {
  const res = await api.patch<EmailTemplate>(`/emails/templates/${templateId}`, body);
  return res.data;
}

/** Whether compose can overwrite a library template opened from the panel. */
export function canOverwriteLibraryTemplate(
  template: EmailTemplate | null | undefined,
  canEditCompanyTemplates: boolean,
): boolean {
  if (!template?.id || template.folderId == null) return false;
  if (template.permission === 'read') return false;
  if (template.scope === 'COMPANY') return canEditCompanyTemplates;
  if (template.isOwner) return true;
  return template.permission === 'write';
}

/** Convert API draft to formData shape (to, cc, bcc as comma-separated strings). */
export function draftToFormData(draft: EmailDraft): {
  accountId: number;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  html: string;
} {
  const html = draft.composeState
    ? draft.bodyHtml ?? draft.composeState.userBody
    : draft.bodyHtml ?? draft.bodyText ?? '';
  return {
    accountId: draft.emailAccountId,
    to: formatAddressesToComma(draft.to),
    cc: formatAddressesToComma(draft.cc),
    bcc: formatAddressesToComma(draft.bcc),
    subject: draft.subject ?? '',
    html,
  };
}

/** Convert API template to formData shape for compose. */
export function templateToFormData(template: EmailTemplate): {
  accountId: number;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  html: string;
} {
  const accountId = template.emailAccountId ?? 0;
  return {
    accountId,
    to: formatAddressesToComma(template.to),
    cc: formatAddressesToComma(template.cc),
    bcc: formatAddressesToComma(template.bcc),
    subject: template.subject?.trim() || template.name?.trim() || '',
    html: template.bodyHtml?.trim() || effectivePlainTextBody(template.bodyText) || '',
  };
}
